import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Play, Shield, Brain, CheckSquare, BarChart3,
  Zap, Eye, Users, Building2, Lock, FileText, Layers, Globe,
  Code, Settings, Server, Briefcase, Heart, Landmark,
  ChevronRight, Mail, Upload, Bell, Sparkles, Radio,
  Video, MonitorPlay, X, Send, Calendar, DollarSign,
  MessageSquare, Wrench, Database
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-60px' }, transition: { duration: 0.5 } };
const stagger = { initial: 'hidden', whileInView: 'visible', viewport: { once: true, margin: '-40px' }, variants: { visible: { transition: { staggerChildren: 0.08 } } } };
const cardVariant = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

/* ═══════════════════════════════════════════════
   D3 Command Official Brand Logo
   ═══════════════════════════════════════════════ */
const D3CommandLogo = ({ size = 'default', className = '' }) => {
  const heights = { sm: 28, default: 36, lg: 44, hero: 64 };
  const h = heights[size] || 36;
  return (
    <img
      src="/d3-logo-transparent.png"
      alt="D3 Command"
      height={h}
      style={{ height: `${h}px`, width: 'auto' }}
      className={className}
      data-testid={`d3-brand-logo-${size}`}
      draggable={false}
    />
  );
};

/* ═══════════════════════════════════════════════
   SECTION: Hero
   ═══════════════════════════════════════════════ */
const Hero = () => {
  const { demoLogin } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);

  const handleViewDemo = async () => {
    setDemoLoading(true);
    try {
      localStorage.removeItem('d3_product_tour');
      localStorage.removeItem('d3_tour_muted');
      sessionStorage.removeItem('d3_tour_auto_triggered');
      await demoLogin();
      window.history.pushState({}, '', '/dashboard');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch { setDemoLoading(false); }
  };

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="mktg-hero">
      <div className="absolute inset-0 bg-[#0B1120]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(37,99,235,0.18),transparent)]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-blue-600/[0.06] rounded-full blur-[140px]" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center py-32">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <button onClick={handleViewDemo} disabled={demoLoading}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] mb-8 hover:bg-white/[0.06] transition-colors cursor-pointer">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <span className="text-xs font-medium text-gray-400 tracking-wide">
              {demoLoading ? 'Loading demo...' : 'Try our Interactive Demo \u2014 No signup required'}
            </span>
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08 }}
          className="mb-8 flex justify-center" data-testid="mktg-hero-logo">
          <D3CommandLogo size="hero" />
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6">
          AI-Powered Apps, Automation,{' '}
          <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">and Custom Software Solutions</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
          We help visionary leaders and enterprise teams build high-performance web and mobile apps, automate workflows, and deploy with confidence.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-wrap items-center justify-center gap-4">
          <button onClick={handleViewDemo} disabled={demoLoading}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm shadow-[0_0_24px_rgba(79,70,229,0.25)] hover:shadow-[0_0_32px_rgba(79,70,229,0.4)] transition-all" data-testid="mktg-hero-try-demo">
            <Play className="w-3.5 h-3.5" /> {demoLoading ? 'Loading...' : 'Try Interactive Demo'}
          </button>
          <a href="#demo" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white/[0.06] border border-white/10 hover:border-white/20 hover:bg-white/[0.08] text-white font-medium text-sm transition-all" data-testid="mktg-hero-get-quote">
            Get a Custom Quote <ArrowRight className="w-4 h-4" />
          </a>
          <a href="#services" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white/[0.04] border border-white/[0.06] hover:border-indigo-500/30 text-gray-300 hover:text-white font-medium text-sm transition-all">
            Explore Services <ChevronRight className="w-4 h-4" />
          </a>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }}
          className="flex items-center justify-center gap-6 mt-8 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><MonitorPlay className="w-3.5 h-3.5" /> Auto-narrated walkthrough</span>
          <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Live AI insights</span>
          <span className="flex items-center gap-1.5"><Radio className="w-3.5 h-3.5" /> Real-time sync</span>
        </motion.div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════
   SECTION: Flagship Platform
   ═══════════════════════════════════════════════ */
const platformSteps = [
  { icon: Upload, label: 'Upload', desc: 'Meeting or audio' },
  { icon: Brain, label: 'AI Processing', desc: 'Transcribe & analyze' },
  { icon: CheckSquare, label: 'Tasks & Risks', desc: 'Auto-extracted' },
  { icon: BarChart3, label: 'Dashboard', desc: 'Real-time visibility' },
  { icon: Bell, label: 'Alerts', desc: 'AI-powered insights' },
];

const platformFeatures = [
  { icon: Brain, title: 'AI Meeting Intelligence', desc: 'Transcribe conversations and extract key points, decisions, and action items automatically.' },
  { icon: Sparkles, title: 'Live AI Insights', desc: 'Real-time productivity alerts, risk patterns, and context-aware recommendations that surface as you work.' },
  { icon: Zap, title: 'One-Click Quick Actions', desc: 'Upload meetings, create tasks, log risks, or add policies instantly \u2014 streamlined workflows in one click.' },
  { icon: Radio, title: 'Real-Time Collaboration', desc: 'Live presence indicators, WebSocket sync, and instant updates across all team members.' },
  { icon: MessageSquare, title: 'Threaded Comments & @Mentions', desc: 'Unlimited-depth threaded discussions with @mention notifications and email reply-to-comment pipeline.' },
  { icon: Mail, title: 'Activity Digest Emails', desc: 'Opt into daily or weekly digests summarizing mentions, completed tasks, and risk updates.' },
];

const FlagshipPlatform = () => {
  const { demoLogin } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);

  const handleDemo = async () => {
    setDemoLoading(true);
    try {
      localStorage.removeItem('d3_product_tour');
      localStorage.removeItem('d3_tour_muted');
      sessionStorage.removeItem('d3_tour_auto_triggered');
      await demoLogin();
      window.history.pushState({}, '', '/dashboard');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch { setDemoLoading(false); }
  };

  return (
    <section id="platform" className="relative py-28 overflow-hidden" data-testid="mktg-platform">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-500/[0.02] to-transparent" />
      <div className="relative max-w-6xl mx-auto px-6">
        <motion.div {...fadeUp} className="text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">Flagship Platform</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">The platform that turns conversations into execution</h2>
          <p className="text-gray-400 max-w-xl mx-auto">From meeting room to dashboard — every decision, task, and risk captured and tracked automatically.</p>
        </motion.div>

        <motion.div {...fadeUp} className="flex items-center justify-center gap-2 mb-20 flex-wrap">
          {platformSteps.map((step, i) => (
            <div key={step.label} className="flex items-center gap-2">
              <div className="flex flex-col items-center gap-2 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06] min-w-[110px]">
                <step.icon className="w-5 h-5 text-indigo-400" />
                <span className="text-xs font-semibold text-white">{step.label}</span>
                <span className="text-[10px] text-gray-500">{step.desc}</span>
              </div>
              {i < platformSteps.length - 1 && <ArrowRight className="w-4 h-4 text-gray-700 flex-shrink-0" />}
            </div>
          ))}
        </motion.div>

        <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {platformFeatures.map((f) => (
            <motion.div key={f.title} variants={cardVariant} className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-indigo-500/20 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-4 group-hover:bg-indigo-500/15 transition-colors">
                <f.icon className="w-5 h-5 text-indigo-400" />
              </div>
              <h3 className="text-white font-semibold text-sm mb-2">{f.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div {...fadeUp} className="text-center mt-12">
          <button onClick={handleDemo} disabled={demoLoading}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-all shadow-[0_0_24px_rgba(79,70,229,0.2)]" data-testid="mktg-platform-demo-btn">
            <Play className="w-4 h-4" /> {demoLoading ? 'Loading...' : 'Try Interactive Demo'}
          </button>
          <p className="text-xs text-gray-600 mt-3">No signup required &bull; Auto-narrated walkthrough included</p>
        </motion.div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════
   SECTION: Self-Serve Demo
   ═══════════════════════════════════════════════ */
const demoCards = [
  { icon: MonitorPlay, title: 'Watch Demo', desc: 'Auto-narrated 2-minute guided walkthrough with professional voiceover', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { icon: Eye, title: 'Take Tour', desc: 'Step-by-step interactive product tour highlighting key features', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { icon: Video, title: 'Record Demo Video', desc: 'Export your demo session as MP4 to share with stakeholders', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
  { icon: Sparkles, title: 'Live AI Insights', desc: 'Experience real-time AI alerts and productivity recommendations', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
];

const SelfServeDemo = () => {
  const { demoLogin } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);

  const handleDemo = async () => {
    setDemoLoading(true);
    try {
      localStorage.removeItem('d3_product_tour');
      localStorage.removeItem('d3_tour_muted');
      sessionStorage.removeItem('d3_tour_auto_triggered');
      await demoLogin();
      window.history.pushState({}, '', '/dashboard');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch { setDemoLoading(false); }
  };

  return (
    <section className="relative py-28" data-testid="mktg-self-serve-demo">
      <div className="absolute inset-0 bg-[#0D1728]" />
      <div className="relative max-w-5xl mx-auto px-6">
        <motion.div {...fadeUp} className="text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">Self-Serve Demo</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Experience the product before you commit</h2>
          <p className="text-gray-400 max-w-xl mx-auto">Try our fully interactive demo with real AI features — tailored for both small teams and large organizations.</p>
        </motion.div>

        <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {demoCards.map((c) => (
            <motion.div key={c.title} variants={cardVariant}
              className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-indigo-500/20 transition-colors text-center">
              <div className={`w-11 h-11 rounded-xl ${c.bg} flex items-center justify-center mx-auto mb-4`}>
                <c.icon className={`w-5 h-5 ${c.color}`} />
              </div>
              <h3 className="text-white font-semibold text-sm mb-2">{c.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div {...fadeUp} className="text-center">
          <button onClick={handleDemo} disabled={demoLoading}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-all shadow-[0_0_24px_rgba(79,70,229,0.2)]" data-testid="mktg-launch-demo-btn">
            {demoLoading ? 'Loading...' : 'Launch Interactive Demo'}
          </button>
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-600">
            <span>No signup required</span>
            <span>Sample data auto-resets</span>
            <span>Full product access</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════
   SECTION: Services
   ═══════════════════════════════════════════════ */
const services = [
  { icon: Brain, title: 'AI Workflow Automation', desc: 'Stop wasting time on repetitive tasks. We build custom AI agents that handle your heavy lifting and speed up your daily operations.' },
  { icon: Code, title: 'Custom Web & Mobile Apps', desc: 'Build something your users love. We engineer high-performance apps for every device, focusing on speed, design, and usability.' },
  { icon: Settings, title: 'Business Process Digitization', desc: 'Move your workflows into the modern age. We transform manual processes into streamlined digital systems that grow with you.' },
  { icon: Layers, title: 'Operations & Governance', desc: 'Stay in control of your growth. Our platforms provide the tracking and visibility you need to keep your team aligned and compliant.' },
  { icon: BarChart3, title: 'Dashboards & Analytics', desc: 'See your business clearly. We build real-time reporting tools that turn raw data into actionable insights for better decision-making.' },
  { icon: Server, title: 'API & Systems Integration', desc: 'Connect your entire tech stack. We bridge the gap between your CRM, internal tools, and data sources for a unified workflow.' },
  { icon: Globe, title: 'App Store & Cloud Deployment', desc: 'Launch with total confidence. We manage the full release process for iOS, Android, and web, handling every technical and compliance detail.' },
  { icon: Landmark, title: 'Public Sector & Government Solutions', desc: 'Secure, process-driven software for regulated environments. We build for accountability, audit readiness, and cross-team visibility.' },
  { icon: Wrench, title: 'Managed Support & Maintenance', desc: 'Long-term stability for your assets, including specialized technical support tailored for public sector and enterprise security standards.' },
];

const Services = () => (
  <section id="services" className="relative py-28" data-testid="mktg-services">
    <div className="relative max-w-6xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400 mb-3">Services</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Custom software and AI solutions for growth-oriented businesses</h2>
        <p className="text-gray-400 max-w-xl mx-auto">Enterprise-grade solutions from the team behind the D3 Command platform.</p>
      </motion.div>

      <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {services.map((s) => (
          <motion.div key={s.title} variants={cardVariant}
            className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-cyan-500/20 transition-all group">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/15 transition-colors">
              <s.icon className="w-5 h-5 text-cyan-400" />
            </div>
            <h3 className="text-white font-semibold text-sm mb-2">{s.title}</h3>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">{s.desc}</p>
            <a href="#demo" className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors">
              Request Custom Quote <ArrowRight className="w-3 h-3" />
            </a>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Industries
   ═══════════════════════════════════════════════ */
const industries = [
  { icon: Building2, title: 'Enterprise Operations', desc: 'Structured execution for cross-functional teams at scale.' },
  { icon: Shield, title: 'Compliance & Risk', desc: 'Audit-ready workflows with built-in traceability and controls.' },
  { icon: Briefcase, title: 'Financial Services', desc: 'Governance, reporting, and operational visibility for regulated finance.' },
  { icon: Heart, title: 'Healthcare', desc: 'Secure, compliant platforms for care coordination and administration.' },
  { icon: Landmark, title: 'Public Sector & Government', desc: 'Process-driven solutions for contractors and government agencies.' },
  { icon: Code, title: 'Product & Engineering', desc: 'AI-powered sprint management, risk tracking, and delivery accountability.' },
];

const Industries = () => (
  <section id="industries" className="relative py-28" data-testid="mktg-industries">
    <div className="absolute inset-0 bg-[#0D1728]" />
    <div className="relative max-w-6xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">Industries</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Built for teams across enterprise and regulated environments</h2>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {industries.map((ind) => (
          <motion.div key={ind.title} variants={cardVariant}
            className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-indigo-500/20 transition-colors">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
              <ind.icon className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm mb-1">{ind.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">{ind.desc}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Why D3 Command
   ═══════════════════════════════════════════════ */
const benefits = [
  { icon: Zap, title: 'Automate Execution', desc: 'Convert conversations into structured tasks and tracked outcomes.' },
  { icon: Eye, title: 'Gain Visibility', desc: 'Real-time dashboards show exactly where work stands across teams.' },
  { icon: Settings, title: 'Reduce Manual Work', desc: 'Eliminate repetitive documentation, reporting, and status updates.' },
  { icon: Users, title: 'Improve Accountability', desc: 'Every task, decision, and risk is assigned, tracked, and auditable.' },
  { icon: Shield, title: 'Strengthen Governance', desc: 'Role-based access, audit logs, and compliance-ready architecture.' },
  { icon: Layers, title: 'Modernize Operations', desc: 'Replace spreadsheets and manual processes with intelligent workflows.' },
];

const WhyD3 = () => (
  <section className="relative py-28" data-testid="mktg-why">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/[0.015] to-transparent" />
    <div className="relative max-w-6xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400 mb-3">Why D3 Command</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Why leaders choose D3 Command</h2>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {benefits.map((b) => (
          <motion.div key={b.title} variants={cardVariant} className="text-center p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-cyan-500/15 transition-colors">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500/10 to-cyan-500/10 flex items-center justify-center mx-auto mb-4">
              <b.icon className="w-5 h-5 text-cyan-400" />
            </div>
            <h3 className="text-white font-semibold text-sm mb-1.5">{b.title}</h3>
            <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Engagement Models
   ═══════════════════════════════════════════════ */
const engagements = [
  { num: '01', title: 'Rapid Solution Deployment', desc: 'Onboard your team and configure your custom solution with guided enablement for immediate impact.' },
  { num: '02', title: 'Custom Solution Development', desc: 'Build bespoke applications, dashboards, or automation systems tailored to your workflows and data.' },
  { num: '03', title: 'Workflow Integration & Modernization', desc: 'Connect and modernize existing tools, systems, and processes into a unified operational layer.' },
  { num: '04', title: 'Enterprise Enablement & Advisory', desc: 'Strategic guidance on AI adoption, workflow optimization, and operational transformation.' },
];

const HowWeWork = () => (
  <section className="relative py-28" data-testid="mktg-how">
    <div className="absolute inset-0 bg-[#0D1728]" />
    <div className="relative max-w-5xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">Engagement Models</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Flexible engagement models designed for businesses of all scales</h2>
        <p className="text-gray-400 max-w-lg mx-auto">Customized delivery models to fit your specific budget, timeline, and technical requirements.</p>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {engagements.map((e) => (
          <motion.div key={e.num} variants={cardVariant}
            className="flex gap-5 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-indigo-500/20 transition-colors">
            <span className="text-3xl font-bold bg-gradient-to-b from-indigo-400/30 to-transparent bg-clip-text text-transparent flex-shrink-0">{e.num}</span>
            <div>
              <h3 className="text-white font-semibold text-sm mb-1.5">{e.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">{e.desc}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Enterprise Readiness
   ═══════════════════════════════════════════════ */
const trustItems = [
  { icon: Brain, title: 'AI Meeting Intelligence', desc: 'Transcribe, analyze, and extract decisions, tasks, and risks from every conversation automatically.' },
  { icon: BarChart3, title: 'Real-Time Execution Tracking', desc: 'Live dashboards show task progress, risk status, and operational metrics across all teams.' },
  { icon: Building2, title: 'Multi-Tenant Architecture', desc: 'Complete data isolation between organizations with separate environments and access controls.' },
  { icon: Mail, title: 'White-Label Email Branding', desc: 'Customize email display name, reply-to address, logo, accent colors, and footer text per organization.' },
  { icon: Lock, title: 'Role-Based Access Control', desc: 'Granular permissions ensure every user sees only what they should \u2014 Admin, Manager, and Member tiers.' },
  { icon: FileText, title: 'Audit Logging & Compliance', desc: 'Every action is recorded with timestamps, user attribution, and full traceability for GDPR compliance.' },
];

const Trust = () => (
  <section className="relative py-28" data-testid="mktg-trust">
    <div className="relative max-w-6xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">Enterprise Readiness</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Built for secure execution and professional governance</h2>
        <p className="text-gray-400 max-w-lg mx-auto">Access high-level security, compliance, and operational control, regardless of your company size.</p>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {trustItems.map((t) => (
          <motion.div key={t.title} variants={cardVariant}
            className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-indigo-500/15 transition-colors">
            <t.icon className="w-5 h-5 text-indigo-400 mb-3" />
            <h3 className="text-white font-semibold text-sm mb-1.5">{t.title}</h3>
            <p className="text-gray-500 text-xs leading-relaxed">{t.desc}</p>
          </motion.div>
        ))}
      </motion.div>
      <motion.p {...fadeUp} className="text-center mt-8">
        <a href="/login" className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors">Experience it in our interactive demo &rarr;</a>
      </motion.p>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Pricing
   ═══════════════════════════════════════════════ */
const plans = [
  { name: 'Starter', price: '$29', period: '/mo', desc: 'For small teams getting started', features: ['Up to 10 users', 'AI meeting processing', 'Task & risk extraction', 'Real-time dashboards', 'Activity digest emails', 'Email support'], highlight: false },
  { name: 'Growth', price: '$79', period: '/mo', desc: 'For scaling operations teams', features: ['Up to 50 users', 'Everything in Starter', 'Threaded comments & @mentions', 'Advanced analytics', 'Custom integrations', 'Priority support'], highlight: true },
  { name: 'Enterprise', price: 'Custom', period: '', desc: 'For organizations with advanced needs', features: ['Unlimited users', 'Everything in Growth', 'White-label email branding', 'Dedicated environment', 'Custom workflows & SLA', 'Onboarding & training'], highlight: false },
];

const Pricing = () => (
  <section id="pricing" className="relative py-28" data-testid="mktg-pricing">
    <div className="absolute inset-0 bg-[#0D1728]" />
    <div className="relative max-w-5xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400 mb-3">Pricing</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">D3 Command Platform Pricing</h2>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        {plans.map((p) => (
          <motion.div key={p.name} variants={cardVariant}
            className={`p-6 rounded-2xl border transition-colors ${p.highlight ? 'bg-indigo-500/[0.06] border-indigo-500/25' : 'bg-white/[0.02] border-white/[0.06]'}`}>
            <h3 className="text-white font-semibold mb-1">{p.name}</h3>
            <p className="text-gray-500 text-xs mb-4">{p.desc}</p>
            <div className="flex items-baseline gap-1 mb-5">
              <span className="text-3xl font-bold text-white">{p.price}</span>
              {p.period && <span className="text-sm text-gray-500">{p.period}</span>}
            </div>
            <ul className="space-y-2 mb-6">
              {p.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-xs text-gray-400">
                  <CheckSquare className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <a href={p.name === 'Enterprise' ? '#demo' : '/signup'}
              className={`block text-center py-2.5 rounded-xl text-sm font-medium transition-colors ${p.highlight ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : 'bg-white/[0.06] hover:bg-white/[0.1] text-gray-300'}`}>
              {p.name === 'Enterprise' ? 'Contact Sales' : 'Start Free Trial'}
            </a>
          </motion.div>
        ))}
      </motion.div>

      {/* Custom solution banner */}
      <motion.div {...fadeUp}
        className="p-8 rounded-2xl bg-gradient-to-r from-indigo-500/[0.06] to-cyan-500/[0.04] border border-indigo-500/15 text-center">
        <h3 className="text-white font-semibold text-lg mb-2">Need a custom solution?</h3>
        <p className="text-gray-400 text-sm max-w-lg mx-auto mb-5">
          Looking for a bespoke web app, mobile build, or enterprise-wide automation? We offer project-based pricing tailored to your requirements.
        </p>
        <a href="#demo" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors" data-testid="mktg-custom-quote-btn">
          Get a Custom Quote <ArrowRight className="w-4 h-4" />
        </a>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Trusted Technologies
   ═══════════════════════════════════════════════ */
const TrustedTech = () => (
  <section className="relative py-20" data-testid="mktg-trusted-tech">
    <div className="relative max-w-5xl mx-auto px-6 text-center">
      <motion.div {...fadeUp}>
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">Trusted Technologies</p>
        <p className="text-sm text-gray-400 mb-10">Built on enterprise-grade infrastructure and integrations</p>
        <div className="flex items-center justify-center gap-12 flex-wrap opacity-50">
          {['Stripe', 'Google Cloud', 'AWS', 'MongoDB'].map(name => (
            <span key={name} className="text-lg font-semibold text-gray-400 tracking-wide">{name}</span>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Demo / Contact Form
   ═══════════════════════════════════════════════ */
const SERVICE_OPTIONS = [
  'AI Workflow Automation', 'Custom Web & Mobile Apps', 'Business Process Digitization',
  'Operations & Governance', 'Dashboards & Analytics', 'API & Systems Integration',
  'App Store & Cloud Deployment', 'Public Sector & Government Solutions', 'Managed Support & Maintenance',
];

const DemoSection = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    const serviceMatch = hash.match(/[?&]service=([^&]+)/);
    if (serviceMatch) {
      const svc = decodeURIComponent(serviceMatch[1]).replace(/-/g, ' ');
      const match = SERVICE_OPTIONS.find(o => o.toLowerCase().includes(svc));
      if (match) setForm(p => ({ ...p, service: match }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leads/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Submission failed (${res.status})`);
      }
      setSent(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="demo" className="relative py-28" data-testid="mktg-demo">
      <div className="relative max-w-2xl mx-auto px-6">
        <motion.div {...fadeUp} className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to build your next big idea?</h2>
          <p className="text-gray-400">Tell us about your project or request a platform demo. We&apos;ll show you how D3 Command can help.</p>
        </motion.div>
        {sent ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="text-center p-10 rounded-2xl bg-white/[0.03] border border-green-500/20">
            <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckSquare className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="text-white font-semibold text-lg mb-2">Request Received</h3>
            <p className="text-gray-400 text-sm">Our team will be in touch shortly.</p>
          </motion.div>
        ) : (
          <motion.form {...fadeUp} onSubmit={handleSubmit} className="space-y-4 p-8 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="mktg-demo-error">
                {error}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input type="text" required placeholder="Full Name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500/40" data-testid="mktg-demo-name" />
              <input type="email" required placeholder="Work Email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500/40" data-testid="mktg-demo-email" />
            </div>
            <input type="text" required placeholder="Company" value={form.company} onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500/40" data-testid="mktg-demo-company" />
            <select value={form.service} onChange={(e) => setForm(p => ({ ...p, service: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm focus:outline-none focus:border-indigo-500/40 appearance-none"
              style={{ color: form.service ? 'white' : '#4b5563' }}
              data-testid="mktg-demo-service">
              <option value="" className="bg-[#0D1728] text-gray-400">Select a service (optional)</option>
              {SERVICE_OPTIONS.map(s => <option key={s} value={s} className="bg-[#0D1728] text-white">{s}</option>)}
            </select>
            <textarea placeholder="Tell us about your project..." value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} rows={3}
              className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500/40 resize-none" data-testid="mktg-demo-message" />
            <button type="submit" disabled={submitting} className="w-full py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors" data-testid="mktg-demo-submit">
              {submitting ? 'Submitting...' : 'Send Inquiry'}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════
   SECTION: Final CTA
   ═══════════════════════════════════════════════ */
const FinalCTA = () => (
  <section className="relative py-24" data-testid="mktg-final-cta">
    <div className="absolute inset-0 bg-gradient-to-t from-[#0D1728] to-transparent" />
    <div className="relative max-w-3xl mx-auto px-6 text-center">
      <motion.div {...fadeUp}>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to build something great?</h2>
        <p className="text-gray-400 mb-8 max-w-lg mx-auto">From AI-powered platforms to custom web and mobile apps — D3 Command is your execution partner.</p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a href="#demo" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-all shadow-[0_0_24px_rgba(79,70,229,0.2)]">
            Get a Quote <ArrowRight className="w-4 h-4" />
          </a>
          <a href="mailto:support@d3command.com" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-white/[0.06] border border-white/10 hover:border-white/20 text-white font-medium text-sm transition-all">
            <Mail className="w-4 h-4" /> Talk to Us
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: AI Chatbot Widget
   ═══════════════════════════════════════════════ */
const D3AIChatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: `\u{1f44b} Welcome to D3 Command.\n\nI'm your AI solutions advisor.\n\nI can help you:\n\u2022 Build AI-powered applications\n\u2022 Design custom software\n\u2022 Automate business processes\n\u2022 Develop web & mobile apps\n\u2022 Discuss enterprise technology\n\u2022 Explore government technology solutions\n\u2022 Estimate project scope\n\nHow can I help you today?`,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const quickActions = [
    { icon: Zap, label: 'Build an AI App' },
    { icon: Code, label: 'Custom Software' },
    { icon: Settings, label: 'Workflow Automation' },
    { icon: Landmark, label: 'Government Solutions' },
    { icon: Calendar, label: 'Book a Consultation' },
    { icon: DollarSign, label: 'Request a Quote' },
  ];

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    const userMsg = { role: 'user', text, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/ai/marketing-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history: messages.slice(-6).map(m => ({ role: m.role, content: m.text })) }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: data.response || "Thanks for reaching out! Our team can help with that. Please fill out the contact form above or email support@d3command.com for a detailed consultation.",
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: "Thanks for your interest! For detailed project discussions, please fill out our contact form above or email support@d3command.com. We'll get back to you promptly.",
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white flex items-center justify-center shadow-[0_4px_20px_rgba(79,70,229,0.4)] transition-all hover:scale-105"
        data-testid="mktg-chatbot-toggle"
      >
        {open ? <X className="w-5 h-5" /> : (
          <span className="text-xs font-bold leading-none">
            <span className="text-[10px]">D3</span><span className="text-[8px] opacity-70">&bull;C</span>
          </span>
        )}
      </button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-50 w-[380px] max-h-[520px] rounded-2xl bg-[#0f1729] border border-white/[0.08] shadow-[0_8px_40px_rgba(0,0,0,0.5)] flex flex-col overflow-hidden"
            data-testid="mktg-chatbot-panel"
          >
            {/* Header */}
            <div className="flex items-center gap-3 p-4 border-b border-white/[0.06] overflow-hidden">
              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                <span className="text-[9px] font-bold text-white">D3<span className="text-[7px] opacity-70">&bull;C</span></span>
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-semibold">D3 Command AI</p>
                <p className="text-xs text-gray-500"><span className="text-emerald-400">&#9679;</span> Online 24/7 &bull; Helping businesses build smarter</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-indigo-600 text-white rounded-br-md'
                      : 'bg-white/[0.04] border border-white/[0.06] text-gray-300 rounded-bl-md'
                  }`}>
                    <p className="whitespace-pre-wrap">{m.text}</p>
                    <p className={`text-[10px] mt-1.5 ${m.role === 'user' ? 'text-indigo-200' : 'text-gray-600'}`}>{m.time}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/[0.04] border border-white/[0.06] rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick actions */}
            {messages.length <= 1 && (
              <div className="px-4 pb-3">
                <p className="text-[10px] text-gray-600 mb-2">Quick actions</p>
                <div className="grid grid-cols-2 gap-2">
                  {quickActions.map(a => (
                    <button key={a.label} onClick={() => sendMessage(a.label)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-indigo-500/20 text-xs text-gray-400 hover:text-white transition-colors text-left"
                      data-testid={`chatbot-action-${a.label.toLowerCase().replace(/\s/g, '-')}`}>
                      <a.icon className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-3 border-t border-white/[0.06]">
              <form onSubmit={(e) => { e.preventDefault(); sendMessage(input); }} className="flex items-center gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about AI, software, or automation..."
                  className="flex-1 px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500/30"
                  data-testid="mktg-chatbot-input"
                />
                <button type="submit" disabled={!input.trim() || loading}
                  className="w-10 h-10 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-30 text-white flex items-center justify-center transition-colors"
                  data-testid="mktg-chatbot-send">
                  <Send className="w-4 h-4" />
                </button>
              </form>
              <p className="text-[10px] text-gray-600 text-center mt-2">D3 AI</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/* ═══════════════════════════════════════════════
   SECTION: Footer
   ═══════════════════════════════════════════════ */
const footerLinks = {
  Product: [
    { label: 'Platform', href: '#platform' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'View Demo', href: '/login' },
    { label: 'Login', href: '/login' },
  ],
  Services: [
    { label: 'AI Automation', href: '#services' },
    { label: 'Custom Development', href: '#services' },
    { label: 'Modernization', href: '#services' },
    { label: 'Integration', href: '#services' },
  ],
  Company: [
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '#demo' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Service', href: '/terms-of-service' },
  ],
};

const MktgFooter = () => (
  <footer className="relative border-t border-white/[0.05] bg-[#0B1120] py-16" data-testid="mktg-footer">
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
        <div className="col-span-2 md:col-span-1">
          <div className="mb-4" data-testid="mktg-footer-logo">
            <D3CommandLogo size="sm" />
          </div>
          <p className="text-gray-600 text-xs leading-relaxed mb-4">AI-powered execution, automation, and enterprise software solutions.</p>
          <div className="space-y-1.5 text-xs text-gray-500">
            <div className="flex items-center gap-2"><Mail className="w-3 h-3" /> support@d3command.com</div>
            <div className="flex items-center gap-2"><Globe className="w-3 h-3" /> d3command.com</div>
          </div>
        </div>
        {Object.entries(footerLinks).map(([title, links]) => (
          <div key={title}>
            <h4 className="text-white text-xs font-semibold uppercase tracking-wider mb-4">{title}</h4>
            <ul className="space-y-2.5">
              {links.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className="text-gray-500 hover:text-gray-300 text-xs transition-colors">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/[0.05] pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-center sm:text-left" data-testid="mktg-footer-legal">
          <p className="text-xs text-gray-500">&copy; 2026 D3 Command. All rights reserved.</p>
          <p className="text-[11px] text-gray-600 mt-0.5">D3 Command is a product of D3 Global Holdings LLC.</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <a href="/privacy-policy" className="hover:text-gray-400 transition-colors">Privacy</a>
          <a href="/terms-of-service" className="hover:text-gray-400 transition-colors">Terms</a>
          <a href="/cookie-policy" className="hover:text-gray-400 transition-colors">Cookies</a>
        </div>
      </div>
    </div>
  </footer>
);

/* ═══════════════════════════════════════════════
   NAVBAR (Marketing)
   ═══════════════════════════════════════════════ */
const MktgNav = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0B1120]/90 backdrop-blur-xl border-b border-white/[0.05]' : ''}`} data-testid="mktg-nav">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center" data-testid="mktg-nav-logo">
          <D3CommandLogo size="default" />
        </a>
        <div className="hidden md:flex items-center gap-8">
          <a href="#platform" className="text-xs text-gray-400 hover:text-white transition-colors">Platform</a>
          <a href="#services" className="text-xs text-gray-400 hover:text-white transition-colors">Services</a>
          <a href="#industries" className="text-xs text-gray-400 hover:text-white transition-colors">Industries</a>
          <a href="#pricing" className="text-xs text-gray-400 hover:text-white transition-colors">Pricing</a>
        </div>
        <div className="flex items-center gap-3">
          <a href="/login" className="text-xs text-gray-400 hover:text-white transition-colors hidden sm:block">Sign In</a>
          <a href="#demo" className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold transition-colors" data-testid="mktg-nav-demo">
            Get a Quote
          </a>
        </div>
      </div>
    </nav>
  );
};

/* ═══════════════════════════════════════════════
   MAIN PAGE EXPORT
   ═══════════════════════════════════════════════ */
export default function MarketingPage() {
  return (
    <div className="min-h-screen bg-[#0B1120] text-white" data-testid="marketing-page">
      <MktgNav />
      <Hero />
      <FlagshipPlatform />
      <SelfServeDemo />
      <Services />
      <Industries />
      <WhyD3 />
      <HowWeWork />
      <Trust />
      <Pricing />
      <TrustedTech />
      <DemoSection />
      <FinalCTA />
      <MktgFooter />
      <D3AIChatbot />
    </div>
  );
}
